import { logger } from 'Services/logger/logger';

import type { BulkBiddingErrorType } from '../error.helpers';
import { BULK_BIDDING_ERROR_TYPES } from '../error.helpers';
import type { ActionableVehicle, GetVehiclesForBulkBidding } from '../useFetchVehiclesForBulkBiddingCSV';

import type { BulkBidResult, FailedBid, SuccessfulBid } from './useBulkBidding.types';

interface CreateBulkBidResultParams {
	results: (SuccessfulBid | FailedBid)[];
	unactionableVehicles: GetVehiclesForBulkBidding['unactionable'];
}

export const createBulkBidResult = ({ results, unactionableVehicles }: CreateBulkBidResultParams): BulkBidResult => {
	const { failedBids, successfulBids } = results.reduce<BulkBidResult>(
		(acc, result) => {
			if (result.status === 'success') {
				acc.successfulBids.push(result);
			} else {
				acc.failedBids.push(result);
			}
			return acc;
		},
		{ failedBids: [], successfulBids: [] },
	);

	failedBids.push(
		...unactionableVehicles.map(
			({ bidAmount, vehicle }): FailedBid => ({
				bidAmount,
				reason: BULK_BIDDING_ERROR_TYPES.INVALID_FORMAT,
				status: 'failure',
				vehicle,
			}),
		),
	);

	return { failedBids, successfulBids };
};

export const logBidResults = ({
	failedBids,
	successfulBids,
}: {
	failedBids: FailedBid[];
	successfulBids: SuccessfulBid[];
}) => {
	logger.info({
		context: {
			failedBidReasons: failedBids.map((bid) => ({ enquiryId: bid.vehicle.id, reason: bid.reason })),
			successfulBidIds: successfulBids.map((bid) => bid.bid.id),
		},
		message: 'Bulk bidding results',
		scope: 'useBulkBidding',
	});
};

export const createFailedBid = ({
	actionableVehicle,
	reason,
}: {
	actionableVehicle: ActionableVehicle;
	reason: BulkBiddingErrorType;
}): FailedBid => ({
	bidAmount: actionableVehicle.bidAmount,
	reason,
	status: 'failure',
	vehicle: actionableVehicle.vehicle,
});

export const isFailedBid = (bid: SuccessfulBid | FailedBid): bid is FailedBid => {
	return bid.status === 'failure';
};
